/**
 * The evaluation of a `change_instance_validator` AI processor, as the API
 * describes it.
 *
 * Two halves that are deliberately not the same object: the *cases* are input,
 * carried inside `extra_data` and written back with it, while the *result* is
 * the read-only aggregate of the latest run, on the detail endpoint only. The
 * result carries its own copy of every case it graded, so nothing here is ever
 * matched up by position against the case list — see EvaluationResultRow.
 */

/** a declaration the validator is tried against, and the verdict it should reach */
export interface EvaluationCase {
    /** the consumer declaration — the API insists on a JSON object */
    declaration: Record<string, any>;
    /** true = should be approved, false = should be rejected */
    is_valid: boolean;
}

/**
 * The aggregate state of a run.
 *
 * `null` before the first run; `WAITING_FOR_RESPONSE` while any row is queued
 * or in flight; `FAILED` if any row failed; `OK` once every row answered.
 */
export type EvaluationState = 'WAITING_FOR_RESPONSE' | 'FAILED' | 'OK';

/** the state of one graded row — a run is scheduled a row at a time */
export type EvaluationRowState = 'SCHEDULED' | 'WAITING_FOR_RESPONSE' | 'OK' | 'FAILED';

/** one case as it was graded: the snapshot the run took, plus the verdict */
export interface EvaluationRowResult {
    /** the AIProcessorResponse this row was graded from */
    response_id: number;
    /** position in `extra_data.evaluation` *at the time the run started* */
    index: number;
    state: EvaluationRowState;
    /** the declaration the run was given — not read back off the case list */
    declaration: Record<string, any>;
    /** the verdict the case asked for */
    expected: boolean;
    /** what the model actually said; null while pending, or when it did not answer with a boolean */
    actual: boolean | null;
    /** null while pending */
    passed: boolean | null;
    /** why `actual` is not a verdict, when it is not */
    error: string | null;
    short_log: string | null;
    detailed_log: string | null;
}

/**
 * The latest run. Earlier runs are kept server-side but not reported.
 *
 * `accuracy` is a fraction in [0, 1] **only** while `state` is `OK`, and null
 * in every other state — including a half-finished run, so a run in progress
 * can never report a misleading score. Format it through
 * `formatAccuracy`, never as a bare number.
 */
export interface EvaluationResult {
    run_id: string | null;
    state: EvaluationState | null;
    accuracy: number | null;
    total: number;
    passed: number;
    /** ISO 8601 */
    started: string | null;
    /** ordered by `index`; the only description of what ran */
    results: EvaluationRowResult[];
}

/** what the detail endpoint reports before anything has been run */
export const EMPTY_EVALUATION_RESULT: EvaluationResult = {
    run_id: null,
    state: null,
    accuracy: null,
    total: 0,
    passed: 0,
    started: null,
    results: [],
};

/**
 * `evaluation_result` as it arrives, made safe to render.
 *
 * The list endpoint reports it as `null` and react-admin will hand a form the
 * record the *list* returned, so "no result" and "not loaded yet" both reach
 * the UI as an absent object rather than the empty one the detail endpoint
 * describes.
 */
export const readEvaluationResult = (record: any): EvaluationResult => {
    const raw = record?.evaluation_result;
    if (!raw || typeof raw !== 'object') return EMPTY_EVALUATION_RESULT;
    return {
        ...EMPTY_EVALUATION_RESULT,
        ...raw,
        results: Array.isArray(raw.results) ? raw.results : [],
    };
};

/** the cases as stored — `extra_data.evaluation`, never a top-level field */
export const readEvaluationCases = (record: any): EvaluationCase[] => {
    const raw = record?.extra_data?.evaluation;
    return Array.isArray(raw) ? raw : [];
};
